import axios, { AxiosInstance } from "axios";
import nookies from "nookies";
import { clearCreditTokenOnSignOut } from "./utils/credit-token";

type FetchOptions = RequestInit & { skipAuth?: boolean };
type NookieCtx = Parameters<typeof nookies.get>[0] & Parameters<typeof nookies.set>[0];

// Browser callers share a session; SSR calls share only within their request.
const browserScope = {};
const refreshes = new WeakMap<object, { token: string; promise: Promise<boolean> }>();
const scopeFor = (ctx: NookieCtx) => ctx?.req ?? ctx ?? browserScope;

export const createAuthenticatedAxios = (ctx?: NookieCtx): AxiosInstance => {
  const instance = axios.create({ baseURL: process.env.NEXT_PUBLIC_BASE_URL });
  instance.interceptors.request.use(config => {
    const cookies = nookies.get(ctx);
    if (cookies.bearerToken) config.headers.Authorization = `Bearer ${cookies.bearerToken}`;
    config.headers["Accept-Language"] = cookies.locale || "si";
    return config;
  });
  instance.interceptors.response.use(response => response, async error => {
    if (error.response?.status !== 401 || !error.config || error.config._retryCount) {
      // A failed endpoint (including a retry) is not proof the current session
      // is invalid. Only rejection of its refresh token may clear credentials.
      return Promise.reject(error);
    }
    const current = nookies.get(ctx).bearerToken;
    const sent = error.config.headers?.Authorization;
    const alreadyRotated = !!current && sent !== `Bearer ${current}`;
    if (alreadyRotated || await refreshToken(ctx)) {
      error.config._retryCount = true;
      return instance.request(error.config);
    }
    return Promise.reject(error);
  });
  return instance;
};

export async function authFetch(
  url: string, options: FetchOptions = {}, ctx: NookieCtx = null, retryCount = 0,
): Promise<Response> {
  const fullURL = `${process.env.NEXT_PUBLIC_BASE_URL}${url}`;
  const { skipAuth, ...requestOptions } = options;
  if (skipAuth) return fetch(fullURL, requestOptions);
  const sent = nookies.get(ctx).bearerToken;
  let response = await fetchWithToken(fullURL, requestOptions, sent);
  if (response.status === 401 && retryCount === 0) {
    const current = nookies.get(ctx).bearerToken;
    if ((!!current && current !== sent) || await refreshToken(ctx)) {
      response = await fetchWithToken(fullURL, requestOptions, nookies.get(ctx).bearerToken);
    }
  }
  return response;
}

function fetchWithToken(url: string, options: RequestInit, token?: string): Promise<Response> {
  const headers = new Headers(options.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return fetch(url, { ...options, headers });
}

export function refreshToken(ctx: NookieCtx = null): Promise<boolean> {
  const cookies = nookies.get(ctx);
  const token = cookies.refreshToken;
  if (!token) return Promise.resolve(false);
  const scope = scopeFor(ctx);
  const existing = refreshes.get(scope);
  if (existing?.token === token) return existing.promise;

  const sameSession = () => {
    const current = nookies.get(ctx);
    return current.refreshToken === token && current.bearerToken === cookies.bearerToken;
  };
  const promise = (async () => {
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/auth/refresh`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      if (!sameSession()) return false;
      if (response.ok) {
        const data = await response.json();
        if (!sameSession()) return false;
        if (typeof data.access_token !== 'string' || !data.access_token ||
            typeof data.refresh_token !== 'string' || !data.refresh_token) return false;
        const options = { path: "/", sameSite: "lax" as const, secure: process.env.NODE_ENV === "production" };
        nookies.set(ctx, "bearerToken", data.access_token, { ...options, maxAge: 40 * 60 * 60 });
        nookies.set(ctx, "refreshToken", data.refresh_token, { ...options, maxAge: 180 * 24 * 60 * 60 });
        // nookies reads SSR cookies from the request, not Set-Cookie. Keep the
        // request-local view current so its retry uses the rotated credentials.
        if (ctx?.req) ctx.req.headers.cookie = Object.entries({ ...cookies, bearerToken: data.access_token, refreshToken: data.refresh_token })
          .map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('; ');
        return true;
      }
      if (response.status === 401 || response.status === 403) {
        nookies.destroy(ctx, "bearerToken", { path: "/" });
        nookies.destroy(ctx, "refreshToken", { path: "/" });
        // The session is over, so is the credit token it may have held (a
        // no-op on the server, where there is no storage).
        clearCreditTokenOnSignOut();
      }
      return false;
    } catch {
      // Connectivity / backend failures must not sign the customer out.
      return false;
    }
  })();
  refreshes.set(scope, { token, promise });
  void promise.finally(() => { if (refreshes.get(scope)?.promise === promise) refreshes.delete(scope); });
  return promise;
}

export default createAuthenticatedAxios();
